/**
 * TenantAliasesPanel Component
 * Embedded panel for viewing and managing tenant payment aliases
 */

import { useState, useEffect, useCallback } from 'react';
import type { TenantAlias } from '../api/tenant-aliases.api';
import { formatDateTime } from '../utils/date';
import {
  fetchTenantAliases,
  createTenantAlias,
  deactivateTenantAlias,
} from '../api/tenant-aliases.api';

interface TenantAliasesPanelProps {
  tenantId: string;
  defaultLeaseId?: string;
}

const SOURCE_LABELS: Record<string, string> = {
  onboarding: 'Onboarding',
  confirmed_match: 'Confirmed Match',
  manual: 'Manual',
};

export function TenantAliasesPanel({ tenantId, defaultLeaseId }: TenantAliasesPanelProps): JSX.Element {
  const [aliases, setAliases] = useState<TenantAlias[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newAlias, setNewAlias] = useState('');
  const [newLeaseId, setNewLeaseId] = useState(defaultLeaseId ?? '');
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [deactivatingId, setDeactivatingId] = useState<string | null>(null);

  const loadAliases = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await fetchTenantAliases(tenantId);
      setAliases(data.aliases);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load aliases');
    } finally {
      setIsLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    loadAliases();
  }, [loadAliases]);

  const handleSaveAlias = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newAlias.trim()) return;
    try {
      setIsSaving(true);
      setSaveError(null);
      await createTenantAlias(tenantId, { alias: newAlias.trim(), leaseId: newLeaseId });
      setNewAlias('');
      await loadAliases();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save alias');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeactivate = async (alias: TenantAlias) => {
    try {
      setDeactivatingId(alias.id);
      await deactivateTenantAlias(tenantId, alias.id);
      await loadAliases();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to deactivate alias');
    } finally {
      setDeactivatingId(null);
    }
  };

  return (
    <div data-testid="tenant-aliases-panel" className="bg-card rounded-lg p-5 border border-border">
      <h2 className="text-base font-semibold text-foreground mb-4">Payment Aliases</h2>

      {isLoading && <p className="text-sm text-muted-foreground">Loading aliases...</p>}
      {error && <p className="text-sm text-destructive mb-3">{error}</p>}

      {!isLoading && aliases.length === 0 && (
        <p className="text-sm text-muted-foreground mb-4">No payment aliases yet.</p>
      )}

      {aliases.length > 0 && (
        <div className="mb-4 space-y-2">
          {aliases.map((alias) => (
            <div
              key={alias.id}
              data-testid={`alias-row-${alias.id}`}
              className={`flex items-center justify-between p-3 rounded border ${
                alias.isActive ? 'border-border bg-card' : 'border-border bg-accent/50 opacity-60'
              }`}
            >
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate">{alias.alias}</p>
                <div className="flex gap-3 mt-0.5">
                  <span className="text-xs text-muted-foreground">
                    {SOURCE_LABELS[alias.source] ?? alias.source}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {alias.matchCount} match{alias.matchCount !== 1 ? 'es' : ''}
                  </span>
                  {alias.lastMatchedAt && (
                    <span className="text-xs text-muted-foreground">
                      Last: {formatDateTime(alias.lastMatchedAt, { month: 'short', day: 'numeric', year: 'numeric' })}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 ml-3">
                <span className={`text-xs ${alias.isActive ? 'text-success' : 'text-muted-foreground'}`}>
                  {alias.isActive ? 'Active' : 'Inactive'}
                </span>
                {alias.isActive && (
                  <button
                    type="button"
                    data-testid={`deactivate-alias-button-${alias.id}`}
                    onClick={() => handleDeactivate(alias)}
                    disabled={deactivatingId === alias.id}
                    className="px-2 py-1 text-xs font-medium text-destructive border border-destructive/20 rounded hover:bg-destructive/5 disabled:opacity-50 min-h-[32px]"
                  >
                    {deactivatingId === alias.id ? '...' : 'Deactivate'}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <form onSubmit={handleSaveAlias} className="pt-3 border-t border-border">
        <h3 className="text-sm font-medium text-foreground mb-2">Add Alias</h3>
        {saveError && <p className="text-xs text-destructive mb-2">{saveError}</p>}
        <div className="flex gap-2">
          <div className="flex-1">
            <label htmlFor={`alias-input-${tenantId}`} className="sr-only">
              Alias text
            </label>
            <input
              id={`alias-input-${tenantId}`}
              type="text"
              data-testid="add-alias-input"
              value={newAlias}
              onChange={(e) => setNewAlias(e.target.value)}
              placeholder="e.g., John D, J. Doe"
              className="w-full px-3 py-2 text-sm border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
              aria-required="true"
            />
          </div>
          {!defaultLeaseId && (
            <div className="flex-1">
              <label htmlFor={`lease-input-${tenantId}`} className="sr-only">
                Lease ID
              </label>
              <input
                id={`lease-input-${tenantId}`}
                type="text"
                value={newLeaseId}
                onChange={(e) => setNewLeaseId(e.target.value)}
                placeholder="Lease ID"
                className="w-full px-3 py-2 text-sm border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
          )}
          <button
            type="submit"
            data-testid="save-alias-button"
            disabled={isSaving || !newAlias.trim()}
            className="px-4 py-2 text-sm font-medium text-primary-foreground bg-primary rounded-md hover:bg-primary/90 disabled:opacity-50 min-h-[40px] shrink-0"
          >
            {isSaving ? 'Saving...' : 'Add'}
          </button>
        </div>
      </form>
    </div>
  );
}
